import * as THREE from 'three';
import type { Projectile } from './projectiles';
import type { SuperMeter } from './super';
import { VERTICAL_ENGAGEMENT_RANGE } from './spatial';

// Wider than the player's actual hit radius (PLAYER_RADIUS 0.3 + a lob's own
// 0.2 = a real hit at ~0.5) but not so wide it fires from across the street —
// Classic's own graze band (Classic gotcha #42, "within 40px of a bullet
// passing near you" at that game's own scale) is the model: a close, visible
// near-miss, not a generic proximity bonus.
const GRAZE_RADIUS = 1.1;

/**
 * Ported from Classic's graze-super mechanic (gotcha #30/#42): an enemy
 * projectile that passes CLOSE to the player without actually hitting them
 * fills a little super — rewards standing your ground near incoming fire
 * instead of only rewarding hits/kills. Deliberately reads `slideInvulnerable`
 * the same way `resolve.ts`'s real hit-resolution does: a projectile that's
 * being fully evaded by a dodge still visibly passes close, so it still
 * grazes (a graze is a "near", not a "hit" — the i-frame gate that matters
 * here is only "is the player's hit-circle actually clipping it," which
 * `grazed`'s once-per-projectile latch below already keeps disjoint from
 * `resolveProjectileDamageOnPlayer`'s own hit radius).
 */
export function resolveGrazeOnPlayer(active: Projectile[], playerPos: THREE.Vector3, playerRadius: number, superMeter: SuperMeter): void {
  for (const proj of active) {
    // `active` is a snapshot from BEFORE this tick's hit-resolution ran —
    // a projectile that just landed a real hit was deactivated in place
    // (`pool.deactivate` flips `.active`, doesn't remove it from this
    // array), so checking `.active` here is what keeps a genuine hit from
    // ALSO double-counting as a graze the same tick.
    if (!proj.active || proj.owner !== 'enemy' || proj.grazed) continue;
    if (Math.abs(proj.position.y - playerPos.y) > VERTICAL_ENGAGEMENT_RANGE) continue;
    const dist = Math.hypot(proj.position.x - playerPos.x, proj.position.z - playerPos.z);
    if (dist > GRAZE_RADIUS + playerRadius) continue;
    proj.grazed = true; // once per projectile flight, regardless of how many ticks it spends inside the band
    superMeter.onGraze();
  }
}
